// fftPath.js — FFT's movement range, ported from FFTBG's own pathfinder (BATTLE.BIN 0x80174B8C and the
// routines it calls), so the sim moves units exactly where the game would let them. Pure, data-injected.
// Validated against the game itself: tools/emu-battle/pathcheck.mjs replays every captured call of the
// real routine, and pathfuzz.mjs runs it on generated boards through a MIPS interpreter; both diff this
// file against the game panel by panel.
//
// Map: { w, h, tiles }, `tiles` = the game's raw tile table, 2 layers x 256 x 8 bytes (lower layer
// first, index = y * w + x), as FFTBG keeps it at 0x8018F8CC:
//   +0 & 0x3F surface    +2 height (whole steps)    +3 >> 5 water depth, & 0x1F slope height
//   +4 slope type: a 2-bit multiplier per side on the slope height (bits 0-1 +x, 2-3 -x, 4-5 -y, 6-7 +y)
//   +5 & 0x1F thickness (a floor's underside = its top minus 2 x this)
//   +6 & 1 no panel, & 2 no walking, & 0x80 not a place to stop
// Heights are compared in half steps at the EDGE a unit crosses: 2 x height + slope x code(side).
//
// Mover: { x, y, layer, move, jump, f93, f94, f95, st5a, st5c, u6 } — position, Move and Jump, and the
// game's own unit bytes: +0x93..0x95 movement-ability bits, +0x5A / +0x5C statuses (0x5A: 0x40 Float,
// 0x04 Chicken, 0x02 Frog, 0x01 Critical), +6. `others`: [{ x, y, layer, ally, base, top, job }] living
// units in the way (`mountable`: a chocobo ally it could ride); `bodies`: [{ x, y, layer }] dead or mid-Jump units, whose panels can't be stopped on.
// Output: per panel (layer * 256 + y * w + x) the remaining move + 1 (0 = unreachable), the largest
// single step on the best path (the game's 5-byte panel records, bytes 0 and 4) and `dest`, the panels
// the unit may end its move on (tile byte 5 bit 0x20). moveTargets() then gives the AI's reachable set.

const X = 255;
const row = (s) => [...s].map((c) => (c === 'X' ? X : +c));
// Surface move cost per movement mode (SCUS 0x8005EA50, 64 surfaces each; X = impassable, 0 = water,
// whose cost is the map's water cost). Row 0 normal, 1 Fly/Teleport, 2 Levitate/Float, 3 Waterwalking/Swim,
// 4 Ignore Terrain, 5 Lava Walking.
export const SURFACE_COST = [
  row('111111111000112222X111111X01X1111111011111111211111111111111111X'),
  row('111111111111111111111111111111111111111111111111111111111111111X'),
  row('111111111111111111111111111111111111111111111111111111111111111X'),
  row('111111111111111111X111111X01X1111111111111111111111111111111111X'),
  row('1111111111111111112111111X01X1111111111111111111111111111111111X'),
  row('1111111110001122221111111X01X1111111011111111211111111111111111X'),
];
const SURF_LAVA = 18, SURF_NOSTOP = 28, SURF_NONE = 63;

// side order 0 +x, 1 +y, 2 -x, 3 -y: the slope-type shift of the side you LEAVE by and the side you
// ENTER by (the direction handlers at 0x801754D0..0x80175540)
const DIRS = [
  { dx: 1, dy: 0, out: 0, in: 2 },
  { dx: 0, dy: 1, out: 6, in: 4 },
  { dx: -1, dy: 0, out: 2, in: 0 },
  { dx: 0, dy: -1, out: 4, in: 6 },
];

// The pathfinder's parameters, as its init ("0x80174430") derives them from the unit.
export function pathParams(m) {
  let f94 = m.f94 || 0, f95 = m.f95 || 0;
  const f93 = m.f93 || 0, st5a = m.st5a || 0, f95raw = f95;
  let height = 6;
  if (st5a & 0x06) { f94 &= ~0x0c; f95 &= ~0x04; height = 4; }   // Frog / Chicken: no Teleport/Fly, short
  const p20 = !(f95 & 0x88) && (f94 & 0x10) ? 1 : 0;
  let p28 = f95;
  if (f95 & 0x08) { height += 2; p28 = f95 | 0x80; }              // Levitate: floats 2 higher
  if (st5a & 0x40) f95 |= 0x08;                                   // the Float status counts as floating
  let costRow, kind;                                              // kind = the game's +14: 0 Fly, 1 Teleport, 2.. walking
  if (f95 & 0x04) { costRow = 1; kind = 0; }                     // Fly
  else if (f94 & 0x0c) { costRow = 1; kind = 1; }                // Teleport / Teleport 2
  else if (f95 & 0x08) { costRow = 2; kind = 2; }                // Levitate or the Float status
  else if (f95 & 0x20) { costRow = 5; kind = 3; }                // Lava Walking
  else if (f95 & 0x80) { costRow = 3; kind = 4; }                // Waterwalking
  else if (f95 & 0x40) { costRow = 3; kind = 5; }                // Swim
  else if (f95 & 0x10) { costRow = 4; kind = 6; }                // Ignore Terrain
  else { costRow = 0; kind = 7; }
  const noLeap = kind <= 1;                                       // Fly and Teleport don't search leaps
  const deep3Ok = !(p28 & 0xd0), deep4Ok = !(p28 & 0xc0);         // may stop in depth-3 / depth-4+ water
  const p18 = deep3Ok ? 1 : 0, p19 = deep4Ok ? 1 : 0;
  const p17 = !(f95 & 0x8c) && (f94 & 0x10) ? 1 : 0;              // no water at all
  const lavaOk = (f95raw & 0x28) ? 1 : 0;                         // Levitate / Lava Walking may stop on lava (+37)
  let jumpRaw = Math.min(m.jump, 7);
  const leap = jumpRaw >> 1;
  if (kind === 0 || (f93 & 0x02)) jumpRaw = 31;                   // Fly / Ignore Height
  const retreat = (f95 & 0x02) && (st5a & 0x01);                  // FFTBG's Retreat: +4 Move in Critical
  const move = Math.min(m.move + (retreat ? 4 : 0), 124);
  const stopOnAlly = (m.u6 & 0xc0) && !(st5a & 0x06) && !((m.st5c || 0) & 0x20) ? 1 : 0; // the game's +22
  return { height, kind, costRow, noLeap, p17, p18, p19, p20, p28, lavaOk, jumpRaw, jumpLim: 2 * jumpRaw, leap, move, stopOnAlly,
    waterOne: !!(f94 & 0x02), allOne: !!(f94 & 0x01), teleport: !!(f94 & 0x08) };
}

// A map's raw tile table from its src/data/mapTiles.json entry ({ w, h, lower, upper? }, 6 bytes per tile
// in base64: surface, height, depth/slope, slope type, thickness, flags), as the 2 x 256 x 8-byte table the
// pathfinder reads. A missing upper layer is all "no panel".
export function unpackTiles(entry) {
  const tiles = new Uint8Array(4096);
  for (let i = 256; i < 512; i++) tiles[i * 8 + 6] = 1;
  const b64 = (s) => (typeof atob === 'function' ? Uint8Array.from(atob(s), (c) => c.charCodeAt(0)) : new Uint8Array(Buffer.from(s, 'base64')));
  const put = (layer, s) => { const b = b64(s); for (let i = 0; i < entry.w * entry.h; i++) { const o = (layer * 256 + i) * 8, k = i * 6; tiles[o] = b[k]; tiles[o + 2] = b[k + 1]; tiles[o + 3] = b[k + 2]; tiles[o + 4] = b[k + 3]; tiles[o + 5] = b[k + 4]; tiles[o + 6] = b[k + 5]; } };
  put(0, entry.lower); if (entry.upper) put(1, entry.upper);
  return { w: entry.w, h: entry.h, tiles };
}

// The tile table for a map title as the API gives it ("29) Bariaus Valley"): the entry for its number in
// mapTiles.json's data (passed in, so callers load it their own way), unpacked once. null for no number or
// no entry. The sim checks the tiles against the terrain it's given, so a reused number falls back safely.
const unpackedCache = new WeakMap();
export function tilesForMap(title, mapTiles) {
  const m = /^\s*(\d+)\)/.exec(String(title || ''));
  const e = m && mapTiles && mapTiles.maps ? mapTiles.maps[+m[1]] : null;
  if (!e) return null;
  if (!unpackedCache.has(e)) unpackedCache.set(e, unpackTiles(e));
  return unpackedCache.get(e);
}

// The movement-ability bit field at unit +0x93..0x95: movement ability id k (486 = Move+1 .. 508 = Retreat
// in FFTBG's numbering) is bit 0x80 >> ((k - 486) % 8) of byte 0x93 + (k - 486) / 8. Read off FFTBG's own unit
// records (equipped ability at +0x18 next to the bits), e.g. Swim (503) = +0x95 & 0x40.
export const MOVE_ABILITIES = ['Move+1', 'Move+2', 'Move+3', 'Jump+1', 'Jump+2', 'Jump+3', 'Ignore Height', 'Lifefont',
  'Manafont', 'Sonic Step', 'Pilgrimage', 'Landlocked', 'Teleport', 'Teleport 2', null, null,
  'Waterwalking', 'Swim', 'Lava Walking', 'Ignore Terrain', 'Levitate', 'Fly', 'Retreat', null];
export function moveBits(names = []) {
  const b = [0, 0, 0];
  for (const n of names) { const i = MOVE_ABILITIES.indexOf(n); if (i >= 0) b[i >> 3] |= 0x80 >> (i & 7); }
  return { f93: b[0], f94: b[1], f95: b[2] };
}

export function fftReach(map, mover, others = [], opts = {}) {
  const { w, h, tiles } = map;
  const N = 512;
  const P = pathParams(mover);
  const H = P.height;
  const remain = new Uint8Array(N), maxStep = new Uint8Array(N).fill(255), attr = new Uint8Array(N);
  const topRemain = new Uint8Array(16), topMax = new Uint8Array(16).fill(255); // the unit-top nodes (records 512..527)
  const water = P.waterOne ? 1 : (opts.waterCost ?? 1);
  const cost = SURFACE_COST[P.costRow].map((c) => (c === 0 ? water : (P.allOne && c !== X ? 1 : c)));
  const tile = (layer, x, y) => (layer * 256 + y * w + x) * 8;
  const edge = (t, shift) => 2 * tiles[t + 2] + (tiles[t + 3] & 0x1f) * ((tiles[t + 4] >> shift) & 3);
  const inMap = (x, y) => x >= 0 && y >= 0 && x < w && y < h;
  const REV = !!opts.reverse;
  const unitAt = new Map(others.slice(0, 16).map((o, index) => [(o.layer || 0) * 256 + o.y * w + o.x, { ...o, index }]));

  // Panel attributes ("0x80174E84"): 0x02 a slope rising along x on one side only; 0x40 too steep to
  // walk up (that kind at 3+, any other above the unit's Jump); 0x10 a panel the unit may stand on; 0x80
  // no panel; 0x04 a unit; 0x20 a rideable ally (it may stop there; other allies are only walked through).
  for (let p = 0; p < N; p++) {
    if ((p & 255) >= w * h) continue;
    const t = p * 8, type = tiles[t + 4], sl = tiles[t + 3] & 0x1f, dep = tiles[t + 3] >> 5, b6 = tiles[t + 6];
    let a = 0;
    if ((type & 5) === 1 || (type & 5) === 4) { a |= 0x02; if (sl >= 3) a |= 0x40; }
    else if (sl > P.jumpRaw) a |= 0x40;
    if (!(b6 & 3) && !(dep && P.p17) && !(dep >= 4 && P.p19)) a |= 0x10;
    if (b6 & 1) a |= 0x80;
    else if (P.kind === 0 && (b6 & 2) && (tiles[t] & 0x3f) !== SURF_NONE) a |= 0x10; // a flier may land on no-walk tiles
    attr[p] = a;
  }
  for (const o of others) {
    const p = (o.layer || 0) * 256 + o.y * w + o.x;
    let a = attr[p] | 0x04;
    if (o.job === 0x49) { a |= 0x80; a &= ~0x10; }
    if (!o.ally) a = P.kind !== 0 ? (a & 0xc7) : ((a & 0xcf) | 0x08); // a foe: not standable; a flier may pass over its top
    else if (o.mountable) a |= 0x20;                               // an ally it could ride (a chocobo): may stop there
    else a &= ~0x20;                                               // any other ally: walk through, never stop
    attr[p] = a;
  }

  const steep = (p) => (attr[p] & 0x40) !== 0;
  // Can a unit taking off at edge E jump over column (x, y)? ("0x8017622C") Each thing in it — a tile on
  // either layer, or a FOE standing there — spans [bottom, top] (a tile: lower edge minus twice its
  // thickness, higher edge plus twice its depth); none may cut into the jumper's body (E, E + H), and the
  // column must have something at or below E. Allies are jumped over.
  const gapClear = (x, y, d, E) => {
    let lowest = 127;
    const span = (bottom, top) => {
      if (bottom < lowest) lowest = bottom;
      if (E < bottom && bottom < E + H) return false;
      if (E < top && top < E + H) return false;
      if (bottom <= E && top >= E + H) return false;
      return true;
    };
    for (let layer = 0; layer < 2; layer++) {
      const t = tile(layer, x, y);
      if (tiles[t + 6] & 1) continue;
      const a = edge(t, d.out), b = edge(t, d.in);
      if (!span(Math.min(a, b) - 2 * (tiles[t + 5] & 0x1f), Math.max(a, b) + 2 * (tiles[t + 3] >> 5))) return false;
      const u = unitAt.get(layer * 256 + y * w + x);
      if (u && !u.ally && u.base != null) {
        if (E < u.base + 3 && u.base + 3 < E + H) return false;
        if (!span(u.base, u.top)) return false;
      }
    }
    return lowest === 127 || !(E < lowest);
  };
  // Headroom ("0x80176164"): the lowest tile underside above height `lim` in column (x, y), either layer.
  const ceiling = (x, y, shift, lim) => {
    let c = 127;
    for (let layer = 0; layer < 2; layer++) {
      const t = tile(layer, x, y);
      if (tiles[t + 6] & 1) continue;
      const b = edge(t, shift) - 2 * (tiles[t + 5] & 0x1f);
      if (b > lim && b < c) c = b;
    }
    return c;
  };

  const start = (mover.layer || 0) * 256 + mover.y * w + mover.x;
  remain[start] = P.move + 1; maxStep[start] = 0;
  if (P.kind === 1) remain.fill(1);                              // Teleport: every panel (Teleport's Move limit is the AI's)
  else {
    // The search ("0x80174B8C"): Move rounds; each scans the panels in memory order (layer, row, column)
    // and expands every flagged one, clearing its flag first. A panel improved earlier in a scan is
    // expanded later in it, which settles ties between equally long paths.
    const flagged = new Uint8Array(N); flagged[start] = 1;
    for (let round = 0; round < P.move; round++) {
      let any = false;
      for (let p = 0; p < N; p++) {
        if (!flagged[p]) continue;
        flagged[p] = 0; any = true;
        if ((p & 255) >= w * h) continue;
        const layer = p >> 8, x = (p & 255) % w, y = Math.floor((p & 255) / w), ts = tile(layer, x, y);
        // One move onto column (nx, ny) from `src` (this panel, or the top of the unit standing on it), `gap`
        // tiles skipped (0 = a walk): "0x80175958". It tries the panel on each layer, then the top of a unit
        // standing on it (only where the panel is marked 0x08: a foe for a flier, an ally on the upper layer).
        const tryStep = (src, d, nx, ny, gap) => {
          const from = src.edge;
          for (let a0 = 0; a0 < 4; a0++) {
            const nl = a0 & 1, onTop = a0 >> 1, q = nl * 256 + ny * w + nx, td = tile(nl, nx, ny);
            let to, dstCeil, c, k = -1;
            if (!onTop) {
              if (!(attr[q] & 0x10)) continue;                        // not a panel it may enter
              c = cost[tiles[td] & 0x3f];
              if (c === X) continue;
              to = edge(td, d.in);
              dstCeil = ceiling(nx, ny, d.in, to);
            } else {
              if (!(attr[q] & 0x08)) continue;
              const u = unitAt.get(q); if (!u || u.top == null) continue;
              k = u.index; c = 1; to = u.top;
              dstCeil = ceiling(nx, ny, d.in, to - 1);
            }
            if (dstCeil < from + H || src.ceil < to + H || dstCeil < to + H) continue; // no headroom
            if (!onTop) {
              if (edge(td, d.out) > to && steep(q)) continue;        // walking up a too-steep slope
              to += 2 * (tiles[td + 3] >> 5);                         // water depth raises the edge
            }
            if (gap && to > from) continue;                           // a leap never lands higher
            const step = Math.abs(to - from);
            if (step > P.jumpLim) continue;
            const left = src.remain - gap - c;
            if (left <= 0) continue;
            const ms = P.kind === 0 ? 0 : Math.max(step, src.ms === 255 ? 0 : src.ms);
            if (!onTop) {
              if (left < remain[q] || (left === remain[q] && ms >= maxStep[q])) continue;
              remain[q] = left; maxStep[q] = ms;
            } else {
              if (left < topRemain[k] || (left === topRemain[k] && ms >= topMax[k])) continue;
              topRemain[k] = left; topMax[k] = ms;
            }
            if (left >= 2) flagged[q] = 1;
          }
        };
        // REVERSE (the AI's distance-to-target flood, 0x80178CA4): the flood runs from the target outward, so a
        // flood step p -> q stands for the unit's real move q -> p. The real move's checks apply with the ends
        // swapped: q must be left that way (steep: downhill only), p entered (steep: not walking up it), the
        // cost is p's, and a leap may not land higher than it started.
        const tryRev = (d, nx, ny, gap) => {
          // q on either layer, as a forward step tries both (FFTBG's flood from a unit on a bridge walks the bridge,
          // steps onto the wall top beside it and comes down from there)
          for (let nl = 0; nl < 2; nl++) {
            const q = nl * 256 + ny * w + nx, td = tile(nl, nx, ny);
            if (!(attr[q] & 0x10) || (attr[q] & 0x80)) continue;
            const c = cost[tiles[ts] & 0x3f];
            if (c === X || cost[tiles[td] & 0x3f] === X) continue;
            if (steep(q) && edge(td, d.in) >= edge(td, d.out)) continue;      // q is a steep slope it can't leave this way
            let to = edge(ts, d.out);
            if (steep(p) && edge(ts, d.in) > to) continue;                     // walking up into p's steep slope
            to += 2 * (tiles[ts + 3] >> 5);
            let from = edge(td, d.in); const dep = tiles[td + 3] >> 5; if (dep && (P.p28 & 0xc0)) { from += 2 * dep; if (P.p28 & 0x40) from -= 2; }
            if (ceiling(nx, ny, d.in, edge(td, d.in)) < to + H || ceiling(x, y, d.out, to - 2 * (tiles[ts + 3] >> 5)) < from + H) continue;
            if (gap && to > from) continue;
            // a leap's gap must be clear of the jumper as it takes off, from q (the real move's start): FFTBG's flood
            // didn't leap off a pillar top under the arch beside it, which a check at p's height let through
            if (gap) { let clear = true; for (let k = 1; k <= gap && clear; k++) clear = gapClear(x + k * d.dx, y + k * d.dy, d, from); if (!clear) continue; }
            const step = Math.abs(to - from);
            if (step > P.jumpLim) continue;
            const left = remain[p] - gap - c;
            if (left <= 0) continue;
            const ms = P.kind === 0 ? 0 : Math.max(step, maxStep[p] === 255 ? 0 : maxStep[p]);
            if (left < remain[q] || (left === remain[q] && ms >= maxStep[q])) continue;
            remain[q] = left; maxStep[q] = ms;
            if (left >= 2) flagged[q] = 1;
          }
        };
        if (REV) {
          for (const d of DIRS) { const nx = x + d.dx, ny = y + d.dy; if (remain[p] >= 2 && inMap(nx, ny)) tryRev(d, nx, ny, 0); }
          if (P.noLeap) continue;
          for (const d of DIRS) {
            if (remain[p] < 2) continue;
            for (let g = 1; g <= P.leap; g++) {
              if (remain[p] - g < 2) break;
              const gx = x + g * d.dx, gy = y + g * d.dy, lx = x + (g + 1) * d.dx, ly = y + (g + 1) * d.dy;
              if (!inMap(gx, gy) || !inMap(lx, ly)) break; // (the gap is checked in tryRev, from the take-off)
              tryRev(d, lx, ly, g);
            }
          }
          continue;
        }
        const canLeave = (d) => !(steep(p) && edge(ts, d.out) >= edge(ts, d.in)); // a steep slope: downhill only
        // the edge it leaves from: a floater or water walker (+28 & 0xC0) is at the water's SURFACE
        const fromEdge = (d) => { let e = edge(ts, d.out); const dep = tiles[ts + 3] >> 5; if (dep && (P.p28 & 0xc0)) { e += 2 * dep; if (P.p28 & 0x40) e -= 2; } return e; };
        const fromPanel = (d) => ({ edge: fromEdge(d), ceil: ceiling(x, y, d.out, edge(ts, d.out)), remain: remain[p], ms: maxStep[p] });
        const here = unitAt.get(p);
        const fromTop = (d) => ({ edge: here.top, ceil: ceiling(x, y, d.out, here.top - 1), remain: topRemain[here.index], ms: topMax[here.index] });
        // directions 0-3 from the panel, then 4-7 from the top of the unit on it (if it's one it can stand on)
        for (const d of DIRS) { const nx = x + d.dx, ny = y + d.dy; if (remain[p] >= 2 && canLeave(d) && inMap(nx, ny)) tryStep(fromPanel(d), d, nx, ny, 0); }
        if ((attr[p] & 0x08) && here && here.top != null) for (const d of DIRS) { const nx = x + d.dx, ny = y + d.dy; if (topRemain[here.index] >= 2 && inMap(nx, ny)) tryStep(fromTop(d), d, nx, ny, 0); }
        if (P.noLeap) continue;
        // Leaps ("0x801764D8"): over 1..Jump/2 gap tiles, landing on the next, while the move allows.
        for (const d of DIRS) {
          if (!canLeave(d) || remain[p] < 2) continue;
          const src = fromPanel(d);
          for (let g = 1; g <= P.leap; g++) {
            if (remain[p] - g < 2) break;
            const gx = x + g * d.dx, gy = y + g * d.dy, lx = x + (g + 1) * d.dx, ly = y + (g + 1) * d.dy;
            if (!inMap(gx, gy) || !gapClear(gx, gy, d, src.edge) || !inMap(lx, ly)) break;
            tryStep(src, d, lx, ly, g);
          }
        }
      }
      if (!any) break;
    }
  }

  // Where it may stop ("0x80175288", after "0x80174DF8" takes standing off the bodies' panels): reached,
  // standable, not a slope of 4+ (or a one-sided x slope of 3+), not marked no-stop, not surface 28, no
  // lava without the lava/float bit, water only as deep as the unit may stand in, not on another unit
  // (unless +22 allows an ally's panel). Its own panel isn't in the set.
  for (const b of (opts.bodies || [])) attr[(b.layer || 0) * 256 + b.y * w + b.x] &= ~0x10;
  const dest = new Uint8Array(N);
  for (let p = 0; p < N; p++) {
    if (p === start || (p & 255) >= w * h) continue;
    const t = p * 8, sl = tiles[t + 3] & 0x1f, dep = tiles[t + 3] >> 5, surf = tiles[t] & 0x3f, b6 = tiles[t + 6], a = attr[p];
    if (P.kind === 1 && (a & 0x20)) { dest[p] = 1; continue; }     // a teleporter may land on a rideable ally
    if (sl >= 3 && (a & 0x02)) continue;
    if (!remain[p] || sl >= 4 || (b6 & 0x80) || surf === SURF_NOSTOP) continue;
    if (dep && P.p20) continue;
    if (dep === 3 && P.p18) continue;
    if (dep >= 4 && P.p19) continue;
    if (surf === SURF_LAVA && !P.lavaOk) continue;
    if ((b6 & 2) || !(a & 0x10)) continue;
    if ((a & 0x04) && (!(a & 0x20) || !P.stopOnAlly)) continue;
    dest[p] = 1;
  }
  return { remain, maxStep, dest, params: P };
}

// The panels the AI may move the unit to ("AI movement/panel data setting", 0x80194944): its
// destinations, within Move (as the crow flies) for Teleport (Teleport 2 has no limit), minus every other
// living unit's panel, plus its own. Returns a Set of panel indices (layer * 256 + y * w + x).
export function moveTargets(map, mover, others = [], opts = {}) { return reachTargets(map, mover, others, opts).targets; }
// The same, with the pathfinder's full answer alongside (`reach`, as fftReach returns it).
export function reachTargets(map, mover, others = [], opts = {}) {
  const r = fftReach(map, mover, others, opts), out = new Set();
  const { w, h } = map;
  for (let p = 0; p < 512; p++) {
    if (!r.dest[p] || (p & 255) >= w * h) continue;
    if (r.params.teleport) { const x = (p & 255) % w, y = Math.floor((p & 255) / w); if (Math.abs(x - mover.x) + Math.abs(y - mover.y) > r.params.move) continue; }
    out.add(p);
  }
  for (const o of others) out.delete((o.layer || 0) * 256 + o.y * w + o.x);
  out.add((mover.layer || 0) * 256 + mover.y * w + mover.x);
  return { reach: r, targets: out };
}
